import { useState, useEffect, useCallback } from "react";
import { supabase } from "../supabaseClient";
import { CONSENT_PURPOSE, CONSENT_VERSION } from "../data/consent";

export const CONSENT_CHANGED_EVENT = "habitos:consent-changed";
export const CHAT_DELETED_EVENT    = "habitos:chat-deleted";

// ─────────────────────────────────────────────
// useConsent — aktuelle Einwilligung laden, erteilen, widerrufen
// Gültig ist nur eine nicht widerrufene Einwilligung zur aktuellen Version.
// ─────────────────────────────────────────────
export function useConsent(userId) {
  const [consent, setConsent] = useState(null);   // Zeile aus public.consents oder null
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    const { data } = await supabase
      .from("consents")
      .select("id, version, granted_at, revoked_at")
      .eq("user_id", userId)
      .eq("purpose", CONSENT_PURPOSE)
      .eq("version", CONSENT_VERSION)
      .is("revoked_at", null)
      .order("granted_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    setConsent(data || null);
    setLoading(false);
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  // Profil und Chat-Sperre halten sich gegenseitig aktuell
  useEffect(() => {
    const onChange = () => load();
    window.addEventListener(CONSENT_CHANGED_EVENT, onChange);
    return () => window.removeEventListener(CONSENT_CHANGED_EVENT, onChange);
  }, [load]);

  const grant = useCallback(async () => {
    const { data, error } = await supabase
      .from("consents")
      .insert({ user_id: userId, purpose: CONSENT_PURPOSE, version: CONSENT_VERSION })
      .select("id, version, granted_at, revoked_at")
      .single();
    if (error) throw error;
    setConsent(data);
    window.dispatchEvent(new Event(CONSENT_CHANGED_EVENT));
  }, [userId]);

  const revoke = useCallback(async () => {
    const { error } = await supabase
      .from("consents")
      .update({ revoked_at: new Date().toISOString() })
      .eq("user_id", userId)
      .eq("purpose", CONSENT_PURPOSE)
      .is("revoked_at", null);
    if (error) throw error;
    setConsent(null);
    window.dispatchEvent(new Event(CONSENT_CHANGED_EVENT));
  }, [userId]);

  return { consent, hasConsent: !!consent, loading, grant, revoke, reload: load };
}
